import { create_payment_intent } from "../utils/Constant";
import {
  getPaymentIntentUrlOverride,
  getStripeMode,
  isStripeLivePublishableKey,
  isStripeTestPublishableKey,
} from "../utils/env";

function resolvePaymentIntentUrl() {
  return getPaymentIntentUrlOverride() || create_payment_intent;
}

function extractClientSecret(data) {
  if (!data || typeof data !== "object") return null;

  const candidates = [
    data.clientSecret,
    data.client_secret,
    data.paymentIntent?.client_secret,
    data.payment_intent?.client_secret,
    data.data?.clientSecret,
    data.data?.client_secret,
    data.data?.paymentIntent?.client_secret,
    data.stripe_response?.client_secret,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.includes("_secret_")) {
      return value;
    }
  }

  return null;
}

function extractLivemode(data) {
  if (!data || typeof data !== "object") return null;
  if (typeof data.livemode === "boolean") return data.livemode;
  if (typeof data.stripe_response?.livemode === "boolean") {
    return data.stripe_response.livemode;
  }
  return null;
}

function validateStripeModeMatch(apiLivemode) {
  if (apiLivemode === null) return;

  if (isStripeLivePublishableKey() && apiLivemode === false) {
    throw new Error(
      "LIVE_PAYMENT_BLOCKED: Your website uses pk_live_ but the server still creates TEST payments (livemode: false). " +
        "The server admin must set sk_live_ on panel.whitelabelmd.com for stripe/create-payment-intent. " +
        "Until then, use pk_test_ in .env for local testing."
    );
  }

  if (isStripeTestPublishableKey() && apiLivemode === true) {
    throw new Error(
      "TEST_PAYMENT_BLOCKED: Your website uses pk_test_ but the server created a LIVE payment. " +
        "Use sk_test_ on the server or switch the site to pk_live_."
    );
  }
}

function extractErrorMessage(data, status) {
  if (!data || typeof data !== "object") {
    return status >= 500
      ? "Payment server error. Please try again later."
      : "Failed to create payment intent.";
  }

  const stripeMsg =
    data?.stripe_error?.error?.message ||
    data?.stripe_error?.message ||
    data?.error?.message;

  if (stripeMsg) {
    if (
      /similar object exists in live mode/i.test(stripeMsg) ||
      /similar object exists in test mode/i.test(stripeMsg)
    ) {
      return `${stripeMsg} Use matching live or test keys on both the website and the payment API.`;
    }
    return stripeMsg;
  }

  if (data.status === false || data.status === 0) {
    return data.message || "Payment intent could not be created.";
  }

  return data.message || data.error || "Failed to create payment intent.";
}

export async function createPaymentIntent({
  email,
  amount,
  currency = "usd",
  productId,
  productName,
  sku,
}) {
  if (!email) {
    throw new Error("Email is required before starting payment.");
  }

  if (!amount || amount < 50) {
    throw new Error("Invalid payment amount.");
  }

  if (!productId) {
    throw new Error("Product is still loading. Please wait and try again.");
  }

  const stripeMode = getStripeMode();
  const payload = {
    email,
    productName: productName || "plan",
    sku: sku || "PLAN-01",
    amount: Math.round(amount),
    currency,
    productId: String(productId),
    stripe_mode: stripeMode,
    mode: stripeMode,
    automatic_payment_methods: { enabled: false },
    payment_method_types: ["card", "klarna", "link", "affirm", "amazon_pay"],
  };

  const response = await fetch(resolvePaymentIntentUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  const clientSecret = extractClientSecret(data);

  if (!response.ok || data.status === false || data.status === 0) {
    throw new Error(extractErrorMessage(data, response.status));
  }

  if (!clientSecret) {
    console.error("create-payment-intent unexpected response:", data);
    throw new Error(
      "Payment intent response did not include a client secret. Check the API response format."
    );
  }

  validateStripeModeMatch(extractLivemode(data));

  return { clientSecret, data };
}
